import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';

import config from '@/config';
import FooterLanguageLinks from '@/components/FooterLanguageLinks';
import type { LocaleLink } from '@/components/Header';
import { FOOTER_PARTNER_LINKS } from '@/libs/footer-partners';
import { getAiChatCopy } from '@/libs/ai-chat-i18n';
import { getAiModelsNavLabel } from '@/libs/ai-models-i18n';
import { Link } from '@/navigation';
import logo from '@/public/icon.png';

type FooterLink = {
  href: string;
  label: string;
  external?: boolean;
};

type FooterProps = {
  localeLinks?: LocaleLink[];
};

const footerLinkClass =
  'inline-flex rounded-sm text-sm leading-6 text-base-content/70 transition-colors duration-200 hover:text-base-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-base-200 active:translate-y-px';

const FooterLinkItem = ({ href, label, external = false }: FooterLink) => {
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={footerLinkClass}
      >
        {label}
      </a>
    );
  }

  return (
    <Link href={href} className={footerLinkClass}>
      {label}
    </Link>
  );
};

const Footer = ({ localeLinks }: FooterProps) => {
  const t = useTranslations('Footer');
  const locale = useLocale();

  const exploreLinks: FooterLink[] = [
    { href: '/about', label: t('About') },
    { href: '/ai-model', label: getAiModelsNavLabel(locale) },
    { href: '/ai-chat', label: getAiChatCopy(locale).chat },
    { href: '/skills', label: 'Agent Skills' },
    { href: '/n8n', label: t('n8nWorkflows') },
    { href: '/domain-search', label: t('domainTools') },
    {
      href: 'https://apps.apple.com/us/app/aitoolly-discover-top-ai-tools/id6760460633',
      label: 'AIToolly iOS App',
      external: true,
    },
  ];

  const resourceLinks: FooterLink[] = [
    { href: '/blog', label: t('Blog') },
    { href: '/videos', label: 'Videos' },
    { href: '/producthunt-daily', label: t('productHuntDaily') },
    { href: '/ai-news', label: t('aiNews') },
    {
      href: 'https://aitoolly1.substack.com/subscribe?utm_source=aitoolly.com&utm_medium=website&utm_campaign=site_footer',
      label: 'Newsletter',
      external: true,
    },
  ];

  const communityLinks: FooterLink[] = [
    ...(config.mailgun.supportEmail
      ? [
          {
            href: `mailto:${config.mailgun.supportEmail}`,
            label: t('Support'),
            external: true,
          },
        ]
      : []),
    {
      href: 'https://github.com/AIToolly/AIToolly-ProductHunt-Daily',
      label: 'GitHub',
      external: true,
    },
    { href: 'https://twitter.com/aitoolly', label: 'Twitter', external: true },
    {
      href: 'https://www.youtube.com/@AIToolly-FindBestAITools',
      label: 'YouTube',
      external: true,
    },
    {
      href: 'https://www.buymeacoffee.com/aitoolly.com',
      label: 'Buy me a coffee',
      external: true,
    },
    {
      href: 'https://www.patreon.com/ImgEdify/posts/meet-aitoolly-to-166240013',
      label: 'Patreon',
      external: true,
    },
  ];

  const linkGroups = [
    { title: t('EXPLORE'), links: exploreLinks },
    { title: t('RESOURCES'), links: resourceLinks },
    { title: t('COMMUNITY'), links: communityLinks },
  ];

  return (
    <footer className="border-t border-base-content/10 bg-base-200 text-base-content">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4 lg:pr-12">
            <Link
              href="/"
              aria-label={`${config.appName} homepage`}
              className="inline-flex items-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-base-200"
            >
              <Image
                src={logo}
                alt=""
                className="h-9 w-9 rounded-full"
                width={36}
                height={36}
              />
              <strong className="text-xl font-extrabold tracking-tight">
                {config.appName}
              </strong>
            </Link>

            <p className="mt-5 max-w-sm text-sm leading-6 text-base-content/70">
              {config.appDescription}
            </p>
          </div>

          <nav
            aria-label="Footer"
            className="grid grid-cols-1 gap-10 md:grid-cols-3 lg:col-span-8"
          >
            {linkGroups.map((group) => (
              <div key={group.title}>
                <h2 className="text-sm font-semibold text-base-content">
                  {group.title}
                </h2>
                <ul className="mt-5 space-y-2.5">
                  {group.links.map((link) => (
                    <li key={link.href}>
                      <FooterLinkItem {...link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="mt-12 border-t border-base-content/10 pt-8 lg:mt-16">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-8">
            <h2 className="text-sm font-semibold text-base-content lg:col-span-4">
              {t('PARTNERS')}
            </h2>
            <ul className="grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-3 lg:col-span-8">
              {FOOTER_PARTNER_LINKS.map((link) => (
                <li key={link.href}>
                  <FooterLinkItem {...link} external />
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-base-content/10 pt-6">
          <FooterLanguageLinks
            ariaLabel={t('LANGUAGES')}
            localeLinks={localeLinks}
          />

          <div className="mt-6 flex flex-col gap-6 text-sm text-base-content/70 lg:flex-row lg:items-center lg:justify-between">
            <p>
              Copyright © {new Date().getFullYear()} {config.appName}. All rights
              reserved.
            </p>

            <nav aria-label={t('LEGAL')}>
              <ul className="flex flex-wrap gap-x-5 gap-y-2">
                <li>
                  <Link href="/tos" className={footerLinkClass}>
                    {t('Terms of services')}
                  </Link>
                </li>
                <li>
                  <Link href="/privacy-policy" className={footerLinkClass}>
                    {t('Privacy policy')}
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
